import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecrutamentoDashboard = ({ token, user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    // Gerar anos disponíveis (2025 até 10 anos no futuro)
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2025; year <= currentYear + 10; year++) {
      years.push(year);
    }
    setAvailableYears(years);
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [selectedYear]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/dashboard-stats?year=${selectedYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar currentPage="recrutamento" user={user} onLogout={onLogout} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>Carregando...</div>
        </div>
      </div>
    );
  }

  // Preparar dados para os gráficos
  const chartData = stats.meses.map((mes, index) => ({
    mes: mes,
    leads: stats.leads_por_mes[index],
    ganhos: stats.ganhos_por_mes[index],
    conversao: stats.conversao_por_mes[index]
  }));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <Sidebar currentPage="recrutamento" user={user} onLogout={onLogout} />
      
      <div style={{ flex: 1, padding: '30px' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1F2937', marginBottom: '8px' }}>
            Dashboard de Recrutamento
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            Visão geral dos leads e conversões
          </p>
        </div>

        {/* Filtro de Ano */}
        <div style={{ marginBottom: '30px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            Filtrar por ano:
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{
              padding: '8px 16px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Cards de Resumo */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px', 
          marginBottom: '30px' 
        }}>
          {/* Card LEADS */}
          <Card style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                LEADS
              </h3>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#3B82F6' }}>
                {stats.total_leads}
              </div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                Total de leads em {selectedYear}
              </p>
            </div>
          </Card>

          {/* Card CONVERSÃO */}
          <Card style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                CONVERSÃO
              </h3>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#10B981' }}>
                {stats.taxa_conversao}%
              </div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                Taxa de conversão
              </p>
            </div>
          </Card>

          {/* Card GANHOS */}
          <Card style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                GANHOS
              </h3>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#F59E0B' }}>
                {stats.total_ganhos}
              </div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                Pessoas que entraram
              </p>
            </div>
          </Card>
        </div>

        {/* Gráfico de Leads */}
        <Card style={{ 
          padding: '24px', 
          backgroundColor: 'white', 
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '20px' }}>
            Leads por Mês - {selectedYear}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="leads" fill="#3B82F6" name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Gráfico de Ganhos */}
        <Card style={{ 
          padding: '24px', 
          backgroundColor: 'white', 
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '20px' }}>
            Ganhos por Mês - {selectedYear}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="ganhos" fill="#10B981" name="Ganhos" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Gráfico de Conversão */}
        <Card style={{ 
          padding: '24px', 
          backgroundColor: 'white', 
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '20px' }}>
            Taxa de Conversão por Mês - {selectedYear}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis 
                label={{ value: '%', position: 'insideLeft' }}
                domain={[0, 100]}
              />
              <Tooltip 
                formatter={(value) => `${value}%`}
              />
              <Legend />
              <Bar dataKey="conversao" fill="#F59E0B" name="Conversão (%)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
};

export default RecrutamentoDashboard;
